/**
 * Commander Design System - Kiosk Theme
 *
 * Clean, professional light theme aligned with the admin dashboard.
 * Optimised for touch interaction with 64px+ touch targets.
 */

import { type MantineThemeOverride, mergeThemeOverrides, rem } from '@mantine/core';
import { commanderTheme } from '@commander/tokens/mantine-theme';
import { sharedTheme } from './shared';
import { touchTargets } from '../tokens/spacing';
import { fontFamily } from '../tokens/typography';

/**
 * Kiosk colour palette - light theme matching admin dashboard
 */
const kioskPalette = {
  /** Page background (Slate-50) */
  background: '#F8FAFC',
  /** Card/panel surface */
  surface: '#FFFFFF',
  /** Secondary surface (Slate-100) */
  surfaceAlt: '#F1F5F9',
  /** Border colour (Slate-200) */
  border: '#E2E8F0',
  /** Primary text (Slate-800) */
  text: '#1E293B',
  /** Secondary text (Slate-500) */
  textMuted: '#64748B',
  /** Success state */
  success: '#22C55E',
  /** Error state */
  error: '#EF4444',
  /** Primary action */
  primary: '#3B82F6',
};

/**
 * Kiosk-specific theme overrides
 */
const kioskOverrides: MantineThemeOverride = {
  /** Use display font for headings */
  headings: {
    fontFamily: fontFamily.display,
    fontWeight: '700',
    sizes: {
      h1: { fontSize: rem(48), lineHeight: '1.1' },
      h2: { fontSize: rem(36), lineHeight: '1.15' },
      h3: { fontSize: rem(28), lineHeight: '1.2' },
      h4: { fontSize: rem(22), lineHeight: '1.25' },
    },
  },

  /** Larger base font size for readability */
  fontSizes: {
    xs: rem(14),
    sm: rem(16),
    md: rem(20),
    lg: rem(24),
    xl: rem(32),
  },

  /** Increased spacing for touch */
  spacing: {
    xs: rem(8),
    sm: rem(12),
    md: rem(20),
    lg: rem(32),
    xl: rem(48),
  },

  other: {
    kioskPalette,
  },

  components: {
    /** Large buttons with clean admin styling */
    Button: {
      defaultProps: {
        size: 'xl',
        radius: 'md',
      },
      styles: {
        root: {
          minHeight: touchTargets.kioskPrimary,
          fontSize: rem(22),
          fontWeight: 500,
          fontFamily: fontFamily.display,
          letterSpacing: '0.01em',
          padding: `${rem(16)} ${rem(40)}`,
          transition: 'all 150ms ease',
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
      },
    },

    /** Large, high-contrast input fields */
    TextInput: {
      defaultProps: {
        size: 'xl',
        radius: 'md',
      },
      styles: {
        input: {
          minHeight: rem(64),
          fontSize: rem(24),
          fontFamily: fontFamily.mono,
          fontWeight: 500,
          textAlign: 'center',
          backgroundColor: kioskPalette.surface,
          border: `1px solid ${kioskPalette.border}`,
          color: kioskPalette.text,
          transition: 'all 150ms ease',
          '&:focus': {
            borderColor: kioskPalette.primary,
            boxShadow: `0 0 0 1px ${kioskPalette.primary}`,
          },
        },
        label: {
          fontSize: rem(16),
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: rem(8),
        },
      },
    },

    /** Cards with clean admin styling */
    Card: {
      defaultProps: {
        padding: 'xl',
        radius: 'md',
      },
      styles: {
        root: {
          backgroundColor: kioskPalette.surface,
          border: `1px solid ${kioskPalette.border}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          transition: 'background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
        },
      },
    },

    /** Clean status badges */
    Badge: {
      defaultProps: {
        size: 'lg',
        radius: 'sm',
      },
      styles: {
        root: {
          padding: `${rem(12)} ${rem(20)}`,
          fontSize: rem(12),
          fontWeight: 500,
          textTransform: 'none',
        },
      },
    },

    /** Full-width modals for kiosk */
    Modal: {
      defaultProps: {
        fullScreen: true,
        radius: 0,
      },
    },

    /** Clear notifications */
    Notification: {
      styles: {
        root: {
          padding: rem(24),
          border: `1px solid ${kioskPalette.border}`,
          borderRadius: rem(8),
          backgroundColor: kioskPalette.surface,
        },
        title: {
          fontSize: rem(24),
          fontWeight: 700,
          fontFamily: fontFamily.display,
        },
        description: {
          fontSize: rem(18),
        },
      },
    },

    /** High-contrast checkboxes */
    Checkbox: {
      styles: {
        input: {
          width: rem(28),
          height: rem(28),
          borderWidth: 2,
          '&:checked': {
            backgroundColor: kioskPalette.primary,
            borderColor: kioskPalette.primary,
          },
        },
        label: {
          fontSize: rem(18),
          fontWeight: 500,
          paddingLeft: rem(12),
        },
      },
    },

    /** Stack with generous spacing */
    Stack: {
      defaultProps: {
        gap: 'lg',
      },
    },

    /** Title styling */
    Title: {
      styles: {
        root: {
          color: kioskPalette.text,
        },
      },
    },

    /** Text styling */
    Text: {
      styles: {
        root: {
          color: kioskPalette.text,
        },
      },
    },
  },
};

/**
 * Complete kiosk theme - clean light aesthetic matching admin dashboard.
 *
 * Derives from the tokens-driven `commanderTheme` (single source of truth in
 * `@commander/tokens`), then layers the kiosk-specific touch-friendly sizing
 * on top.
 */
export const kioskTheme = mergeThemeOverrides(commanderTheme, sharedTheme, kioskOverrides);
